// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {
	defaultReminderPickerSelection,
	isReminderWallTimeValid,
	listReminderTimeSlotKeys,
	nextValidReminderTimeSlotKey,
	parseReminderTimeSlotKey,
	REMIND_ME_DESCRIPTOR,
	reminderTimeSlotKey,
	resolveReminderWallTime,
} from '@app/features/messaging/utils/ReminderTimePresets';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import pickerStyles from '@app/features/ui/components/form/DateTimePickerField.module.css';
import {Combobox, type ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import comboboxStyles from '@app/features/ui/components/form/FormCombobox.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {getFormattedShortDate, getFormattedTime} from '@app/features/user/utils/DateFormatting';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CalendarBlankIcon, CaretDownIcon, ClockIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {useCallback, useMemo, useState} from 'react';
import {Button as AriaButton, Dialog, DialogTrigger, Popover} from 'react-aria-components';
import {DayPicker} from 'react-day-picker';
import styles from './ReminderDateModal.module.css';

const PICK_A_DATE_TO_BE_REMINDED_DESCRIPTOR = msg({
	message: 'Pick a date and time to be reminded about this message.',
	comment: 'Helper text in the reminder date picker modal explaining the action.',
});
const DATE_DESCRIPTOR = msg({
	message: 'Date',
	comment: 'Accessible label for the reminder date dropdown.',
});
const TIME_DESCRIPTOR = msg({
	message: 'Time',
	comment: 'Accessible label for the reminder time dropdown.',
});
const TODAY_DESCRIPTOR = msg({
	message: 'Today',
	comment: 'Reminder date dropdown label for the current calendar day.',
});
const TOMORROW_DESCRIPTOR = msg({
	message: 'Tomorrow',
	comment: 'Reminder date dropdown label for the next calendar day.',
});
const DATE_PICKER_DESCRIPTOR = msg({
	message: 'Date picker',
	comment: 'Accessible label for the reminder calendar popover.',
});

interface ReminderDateModalProps {
	onSubmit: (remindAt: Date) => Promise<void>;
}

function startOfLocalDay(date: Date): Date {
	const next = new Date(date);
	next.setHours(0, 0, 0, 0);
	return next;
}

function addLocalDays(date: Date, days: number): Date {
	const next = startOfLocalDay(date);
	next.setDate(next.getDate() + days);
	return next;
}

function localCalendarParts(date: Date): {year: number; month: number; day: number} {
	return {year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate()};
}

function isSameLocalDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function ReminderDateModal({onSubmit}: ReminderDateModalProps) {
	const {i18n} = useLingui();
	const today = useMemo(() => startOfLocalDay(new Date()), []);
	const tomorrow = useMemo(() => addLocalDays(today, 1), [today]);
	const [initialSelection] = useState(() => defaultReminderPickerSelection());
	const [selectedDate, setSelectedDate] = useState(
		() => new Date(initialSelection.year, initialSelection.month - 1, initialSelection.day),
	);
	const [timeKey, setTimeKey] = useState(initialSelection.timeKey);
	const [dateOpen, setDateOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const selectedParts = useMemo(() => localCalendarParts(selectedDate), [selectedDate]);
	const {hour, minute} = parseReminderTimeSlotKey(timeKey);
	const remindAt = resolveReminderWallTime(selectedParts.year, selectedParts.month, selectedParts.day, hour, minute);
	const canSubmit = isReminderWallTimeValid(selectedParts.year, selectedParts.month, selectedParts.day, hour, minute);
	const dateLabel = useMemo(() => {
		if (isSameLocalDay(selectedDate, today)) {
			return i18n._(TODAY_DESCRIPTOR);
		}
		if (isSameLocalDay(selectedDate, tomorrow)) {
			return i18n._(TOMORROW_DESCRIPTOR);
		}
		return getFormattedShortDate(selectedDate);
	}, [i18n, i18n.locale, selectedDate, today, tomorrow]);
	const timeOptions = useMemo((): Array<ComboboxOption<string>> => {
		const now = new Date();
		return listReminderTimeSlotKeys().map((key) => {
			const slot = parseReminderTimeSlotKey(key);
			const wall = new Date(selectedDate);
			wall.setHours(slot.hour, slot.minute, 0, 0);
			return {
				value: key,
				label: getFormattedTime(wall),
				isDisabled: !isReminderWallTimeValid(
					selectedParts.year,
					selectedParts.month,
					selectedParts.day,
					slot.hour,
					slot.minute,
					now,
				),
			};
		});
	}, [selectedDate, selectedParts.day, selectedParts.month, selectedParts.year, i18n.locale]);
	const handleDaySelect = useCallback(
		(date: Date | undefined) => {
			if (!date) {
				return;
			}
			const nextDate = startOfLocalDay(date);
			const parts = localCalendarParts(nextDate);
			const currentSlot = parseReminderTimeSlotKey(timeKey);
			setDateOpen(false);
			if (isReminderWallTimeValid(parts.year, parts.month, parts.day, currentSlot.hour, currentSlot.minute)) {
				setSelectedDate(nextDate);
				return;
			}
			const nextKey = nextValidReminderTimeSlotKey(parts.year, parts.month, parts.day);
			if (nextKey) {
				setSelectedDate(nextDate);
				setTimeKey(nextKey);
				return;
			}
			const nextDay = addLocalDays(nextDate, 1);
			const nextDayParts = localCalendarParts(nextDay);
			setSelectedDate(nextDay);
			setTimeKey(
				nextValidReminderTimeSlotKey(nextDayParts.year, nextDayParts.month, nextDayParts.day) ??
					reminderTimeSlotKey(0, 0),
			);
		},
		[timeKey],
	);
	const renderTimeValue = useCallback((option: ComboboxOption<string> | null) => {
		if (!option) {
			return null;
		}
		return (
			<span className={styles.valueWithIcon} data-flx="messaging.reminder-date-modal.time-value">
				<ClockIcon weight="bold" data-flx="messaging.reminder-date-modal.time-value-icon" />
				<span className={styles.valueLabel} data-flx="messaging.reminder-date-modal.time-value-label">
					{option.label}
				</span>
			</span>
		);
	}, []);
	const handleClose = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const handleConfirm = useCallback(async () => {
		if (!canSubmit) {
			return;
		}
		setSubmitting(true);
		try {
			await onSubmit(remindAt);
			ModalCommands.pop();
		} finally {
			setSubmitting(false);
		}
	}, [canSubmit, remindAt, onSubmit]);
	const disabledMatcher = useMemo(() => [{before: today}], [today]);
	const calendarClassNames = useMemo(
		() => ({
			root: pickerStyles.rdpRoot,
			months: pickerStyles.rdpMonths,
			month: pickerStyles.rdpMonth,
			nav: pickerStyles.rdpNav,
			month_caption: pickerStyles.rdpMonthCaption,
			caption_label: pickerStyles.rdpCaptionLabel,
			button_previous: pickerStyles.rdpButtonPrevious,
			button_next: pickerStyles.rdpButtonNext,
			chevron: pickerStyles.rdpChevron,
			month_grid: pickerStyles.rdpMonthGrid,
			weekday: pickerStyles.rdpWeekday,
			day: pickerStyles.rdpDay,
			day_button: pickerStyles.rdpDayButton,
			today: pickerStyles.rdpToday,
			selected: pickerStyles.rdpSelected,
			outside: pickerStyles.rdpOutside,
			disabled: pickerStyles.rdpDisabled,
			hidden: pickerStyles.rdpHidden,
		}),
		[],
	);
	return (
		<Modal.Root size="small" centered onClose={handleClose} data-flx="messaging.reminder-date-modal.modal-root">
			<Modal.Header title={i18n._(REMIND_ME_DESCRIPTOR)} data-flx="messaging.reminder-date-modal.modal-header" />
			<Modal.Content data-flx="messaging.reminder-date-modal.modal-content">
				<Modal.ContentLayout data-flx="messaging.reminder-date-modal.modal-content-layout">
					<Modal.Description data-flx="messaging.reminder-date-modal.modal-description">
						{i18n._(PICK_A_DATE_TO_BE_REMINDED_DESCRIPTOR)}
					</Modal.Description>
					<Modal.InputGroup data-flx="messaging.reminder-date-modal.modal-input-group">
						<div className={styles.fields} data-flx="messaging.reminder-date-modal.fields">
							<div className={styles.field} data-flx="messaging.reminder-date-modal.date-field">
								<FocusRing within={true} offset={-2} data-flx="messaging.reminder-date-modal.date-focus-ring">
									<div className={styles.dateTriggerWrap} data-flx="messaging.reminder-date-modal.date-trigger-wrap">
										<DialogTrigger
											isOpen={dateOpen}
											onOpenChange={setDateOpen}
											data-flx="messaging.reminder-date-modal.date-dialog-trigger"
										>
											<AriaButton
												className={clsx(
													comboboxStyles.inputGroup,
													comboboxStyles.inputGroupDefault,
													dateOpen && comboboxStyles.inputGroupOpen,
													styles.dateButton,
												)}
												aria-label={i18n._(DATE_DESCRIPTOR)}
												data-flx="messaging.reminder-date-modal.date-button"
											>
												<span
													className={styles.prefix}
													aria-hidden
													data-flx="messaging.reminder-date-modal.date-prefix"
												>
													<CalendarBlankIcon weight="bold" data-flx="messaging.reminder-date-modal.date-prefix-icon" />
												</span>
												<span
													className={clsx(comboboxStyles.input, comboboxStyles.inputDefault)}
													data-flx="messaging.reminder-date-modal.date-label"
												>
													{dateLabel}
												</span>
												<div
													className={comboboxStyles.actionButtons}
													data-flx="messaging.reminder-date-modal.date-actions"
												>
													<span
														className={clsx(comboboxStyles.triggerButton, dateOpen && comboboxStyles.triggerButtonOpen)}
														aria-hidden
														data-flx="messaging.reminder-date-modal.date-caret"
													>
														<CaretDownIcon
															weight="bold"
															className={comboboxStyles.triggerIcon}
															data-flx="messaging.reminder-date-modal.date-caret-icon"
														/>
													</span>
												</div>
											</AriaButton>
											<Popover
												placement="bottom start"
												offset={8}
												className={pickerStyles.popover}
												data-flx="messaging.reminder-date-modal.date-popover"
											>
												<Dialog
													className={pickerStyles.dialog}
													aria-label={i18n._(DATE_PICKER_DESCRIPTOR)}
													data-flx="messaging.reminder-date-modal.date-dialog"
												>
													<div
														className={pickerStyles.calendarContainer}
														data-flx="messaging.reminder-date-modal.calendar-container"
													>
														<DayPicker
															mode="single"
															selected={selectedDate}
															onSelect={handleDaySelect}
															disabled={disabledMatcher}
															startMonth={today}
															defaultMonth={selectedDate}
															showOutsideDays={true}
															classNames={calendarClassNames}
															data-flx="messaging.reminder-date-modal.day-picker.day-select"
														/>
													</div>
												</Dialog>
											</Popover>
										</DialogTrigger>
									</div>
								</FocusRing>
							</div>
							<div className={styles.field} data-flx="messaging.reminder-date-modal.time-field">
								<Combobox
									aria-label={i18n._(TIME_DESCRIPTOR)}
									value={timeKey}
									options={timeOptions}
									onChange={setTimeKey}
									renderValue={renderTimeValue}
									isSearchable={false}
									menuPlacement="bottom"
									maxMenuHeight={240}
									data-flx="messaging.reminder-date-modal.time-combobox"
								/>
							</div>
						</div>
					</Modal.InputGroup>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer data-flx="messaging.reminder-date-modal.modal-footer">
				<Button variant="secondary" onClick={handleClose} data-flx="messaging.reminder-date-modal.button.close">
					{i18n._(CANCEL_DESCRIPTOR)}
				</Button>
				<Button
					variant="primary"
					onClick={handleConfirm}
					submitting={submitting}
					disabled={!canSubmit}
					data-flx="messaging.reminder-date-modal.button.confirm"
				>
					{i18n._(REMIND_ME_DESCRIPTOR)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
}
